import React, { useMemo, useRef } from 'react';
import type { ReactNode, ComponentType } from 'react';
import { debug, useI18n } from '../client';
import ReactMarkdown from 'react-markdown';

// 简单的插值函数，用于处理defaultValue - 优化：移到组件外部作为纯函数
function interpolateDefaultValue(text: string, params: Record<string, unknown>): string {
    if (!params) return text;

    const prefix = '{';
    const suffix = '}';
    const escape = (v: unknown) => String(v);

    // 支持嵌套对象访问
    const resolveNestedKey = (obj: Record<string, unknown>, path: string): unknown => {
        if (typeof path !== 'string') return '';

        const keys = path.split('.');
        let current = obj;

        for (const key of keys) {
            if (current === undefined || current === null) return path;
            current = (current as any)[key];
        }

        return current !== undefined ? current : path;
    };

    const pattern = new RegExp(`\\${prefix}([\\w.]+)\\${suffix}`, 'g');

    return text.replace(pattern, (_, key) => {
        const value = resolveNestedKey(params, key);
        return value !== key ? escape(value) : `${prefix}${key}${suffix}`;
    });
}

// 参数比较函数 - 优化：用于稳定化params依赖
function shallowEqual(obj1: Record<string, unknown> | undefined, obj2: Record<string, unknown> | undefined): boolean {
    if (obj1 === obj2) return true;
    if (!obj1 || !obj2) return obj1 === obj2;
    
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    
    if (keys1.length !== keys2.length) return false;
    
    for (const key of keys1) {
        if (obj1[key] !== obj2[key]) return false;
    }
    
    return true;
}

export type I18nMessageFormat = 'text' | 'html' | 'markdown' | 'component';

export interface I18nMessageProps<Keys extends string = string> {
    /** 翻译键 - 类型安全，与 @gulibs/vgrove-i18n 生成的类型兼容 */
    id: Keys;
    /** 插值参数 - 支持嵌套对象 */
    params?: Record<string, unknown>;
    /** 默认值（当翻译不存在时使用） */
    defaultValue?: string;
    /** 渲染格式 */
    format?: I18nMessageFormat;
    /** 自定义渲染函数 */
    render?: (text: string) => ReactNode;
    /** 包装元素 */
    as?: keyof React.JSX.IntrinsicElements | ComponentType<Record<string, unknown>>;
    /** 自定义类名 */
    className?: string;
}

/**
 * 内部实现的国际化消息组件（未备忘化） - 优化版
 */
const I18nMessageInternal = <Keys extends string = string>({
    id,
    params,
    defaultValue,
    format = 'text',
    render,
    as: Component = 'span',
    className,
    ...props
}: I18nMessageProps<Keys>) => {

    const { t, isReady } = useI18n<Keys>();

    // 优化：使用ref存储上一次的params，避免浅比较开销
    const prevParamsRef = useRef<Record<string, unknown> | undefined>(params);
    const stableParams = useMemo(() => {
        if (shallowEqual(params, prevParamsRef.current)) {
            return prevParamsRef.current;
        }
        prevParamsRef.current = params;
        return params;
    }, [params]);

    // 获取翻译文本 - 优化：使用稳定的params引用，添加isReady状态检查
    const text = useMemo(() => {
        // 当i18n系统未准备就绪时，优先使用defaultValue避免显示原始id
        if (!isReady && defaultValue) {
            return stableParams ? interpolateDefaultValue(defaultValue, stableParams) : defaultValue;
        }
        
        const translated = t(id, stableParams);
        debug.log("translated", translated)
        if (translated === id && defaultValue) {
            return stableParams ? interpolateDefaultValue(defaultValue, stableParams) : defaultValue;
        }
        return translated;
    }, [id, stableParams, defaultValue, t, isReady]);

    // 优化：稳定化render函数，避免不必要的重新创建
    const stableRender = useMemo(() => render, [render]);

    // 根据格式渲染内容 - 优化：分离静态配置
    const content = useMemo(() => {
        if (stableRender) {
            return stableRender(text);
        }

        switch (format) {
            case 'html':
                return (
                    <span
                        dangerouslySetInnerHTML={{ __html: text }}
                        className={className}
                        {...props}
                    />
                );

            case 'markdown':
                return (
                    <div className={className} {...props}>
                        <ReactMarkdown>{text}</ReactMarkdown>
                    </div>
                );

            case 'component':
                return parseComponentText(text);

            case 'text':
            default:
                return text;
        }
    }, [text, format, stableRender, className, props]);

    if (format === 'html' || format === 'markdown') {
        return <>{content}</>;
    }

    return (
        <Component className={className} {...props}>
            {content}
        </Component>
    );
};

/**
 * 国际化消息组件
 * 
 * 支持泛型语法: <I18nMessage<KeyType> id="key" />
 * 
 * @example
 * // 基本使用
 * <I18nMessage id="welcome" />
 * 
 * // 带泛型类型约束
 * <I18nMessage<'home.title' | 'home.subtitle'> id="home.title" />
 * 
 * // 带参数
 * <I18nMessage id="user.greeting" params={{ name: 'John' }} />
 */
export const I18nMessage = React.memo(I18nMessageInternal) as (<Keys extends string = string>(
    props: I18nMessageProps<Keys>
) => React.ReactElement) & { displayName?: string };

I18nMessage.displayName = 'I18nMessage';

/**
 * 解析包含组件标记的文本
 */
function parseComponentText(text: string): ReactNode {
    // 简单的组件解析器，支持 {Component}内容{/Component} 格式
    const parts: ReactNode[] = [];
    const regex = /{(\w+)}(.*?){\/\1}/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
        // 添加组件前的文本
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }

        // 根据组件名渲染
        const componentName = match[1];
        const componentContent = match[2];

        switch (componentName) {
            case 'strong':
            case 'b':
                parts.push(<strong key={match.index}>{componentContent}</strong>);
                break;
            case 'em':
            case 'i':
                parts.push(<em key={match.index}>{componentContent}</em>);
                break;
            case 'code':
                parts.push(<code key={match.index}>{componentContent}</code>);
                break;
            default:
                // 未知组件，保持原样
                parts.push(match[0]);
        }

        lastIndex = match.index + match[0].length;
    }

    // 添加剩余文本
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }

    return parts.length === 1 ? parts[0] : <>{parts}</>;
}

// 便捷组件
const I18nTitleInternal = <Keys extends string = string>(
    props: Omit<I18nMessageProps<Keys>, 'as' | 'format'> & { level?: 1 | 2 | 3 | 4 | 5 | 6 }
) => {
    const { level = 1, ...rest } = props;
    const Component = useMemo(() => `h${level}` as keyof React.JSX.IntrinsicElements, [level]);
    return <I18nMessage as={Component} format="text" {...rest} />;
};

export const I18nTitle = React.memo(I18nTitleInternal) as (<Keys extends string = string>(
    props: Omit<I18nMessageProps<Keys>, 'as' | 'format'> & { level?: 1 | 2 | 3 | 4 | 5 | 6 }
) => React.ReactElement) & { displayName?: string };

I18nTitle.displayName = 'I18nTitle';

const I18nParagraphInternal = <Keys extends string = string>(
    props: Omit<I18nMessageProps<Keys>, 'as' | 'format'>
) => {
    return <I18nMessage as="p" format="markdown" {...props} />;
};

export const I18nParagraph = React.memo(I18nParagraphInternal) as (<Keys extends string = string>(
    props: Omit<I18nMessageProps<Keys>, 'as' | 'format'>
) => React.ReactElement) & { displayName?: string };

I18nParagraph.displayName = 'I18nParagraph';

const I18nTextInternal = <Keys extends string = string>(
    props: Omit<I18nMessageProps<Keys>, 'as' | 'format'>
) => {
    return <I18nMessage as="span" format="text" {...props} />;
};

export const I18nText = React.memo(I18nTextInternal) as (<Keys extends string = string>(
    props: Omit<I18nMessageProps<Keys>, 'as' | 'format'>
) => React.ReactElement) & { displayName?: string };

I18nText.displayName = 'I18nText';